import { createFileRoute, Link } from "@tanstack/react-router";
import { ogImageMeta } from "@/lib/seo/og-image";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { Button } from "@/components/ui/button";
import { useConsent } from "@/lib/consent/ConsentProvider";

import { pageSchemas } from "@/lib/seo/page-schema";
export const Route = createFileRoute("/cookie-policy")({
  head: () => ({
    meta: [
      { title: "Cookie Policy | Perceptica" },
      { name: "description", content: "How Perceptica uses cookies on our media monitoring platform - what we set, why we set it, and how to control them in your browser." },
      { property: "og:title", content: "Cookie Policy | Perceptica" },
      { property: "og:description", content: "How Perceptica uses cookies on our media monitoring platform - what we set, why we set it, and how to control them in your browser." },

      ...ogImageMeta("en", "/cookie-policy", "Perceptica Cookie Policy"),
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Cookie Policy | Perceptica" },
      { name: "twitter:description", content: "How Perceptica uses cookies on our media monitoring platform - what we set, why we set it, and how to control them in your browser." },
      { property: "og:url", content: "https://perceptica.com/cookie-policy" },
      { property: "og:type", content: "website" },
    ],
  
    scripts: pageSchemas({ locale: "en", path: "/cookie-policy" }),
  }),
  component: CookiePolicyPage,
});

function CookiePolicyPage() {
  const { reopen, acceptAll, rejectAll, consent } = useConsent();
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main id="main" className="mx-auto max-w-[760px] px-6 py-20 lg:px-10 lg:py-28">
        <h1 className="font-display text-[clamp(2rem,3.4vw,2.8rem)] font-medium text-foreground">Cookie Policy</h1>
        <p className="mt-4 text-[14px] text-foreground/70">Last updated: May 2026</p>

        <div className="prose prose-neutral mt-8 max-w-none text-[14.5px] leading-relaxed text-foreground/85">
          <h2 className="font-display mt-10 text-xl">1. Introduction</h2>
          <p>
            Cookies and similar technologies are small files stored on your device when you visit a website.
            We use them to keep the site working, remember your preferences, and, with your consent, to
            understand how the site is used so we can improve it.
          </p>

          <h2 className="font-display mt-8 text-xl">2. Who is responsible</h2>
          <p>
            Perceptica is the data controller for personal data processed through cookies on this site. For
            any questions about this policy contact us at{" "}
            <a href="mailto:privacy@perceptica.com" className="underline underline-offset-2">privacy@perceptica.com</a>.
          </p>

          <h2 className="font-display mt-8 text-xl">3. Categories of cookies we use</h2>
          <ul className="mt-3 space-y-2">
            <li><strong>Strictly necessary</strong> - required for the site to function (security, session, storing your consent choice). Always on; cannot be disabled.</li>
            <li><strong>Analytics</strong> - aggregated, anonymous usage statistics that help us improve performance and content. Set only after consent.</li>
            <li><strong>Marketing</strong> - used to measure campaigns and tailor messaging across channels. Set only after consent.</li>
          </ul>

          <h2 className="font-display mt-8 text-xl">4. Cookies and storage we set</h2>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full border-collapse text-[13.5px]">
              <thead>
                <tr className="border-b border-border/70 text-left text-foreground">
                  <th className="py-2 pr-3 font-medium">Name</th>
                  <th className="py-2 pr-3 font-medium">Purpose</th>
                  <th className="py-2 pr-3 font-medium">Category</th>
                  <th className="py-2 font-medium">Retention</th>
                </tr>
              </thead>
              <tbody className="text-foreground/80">
                <tr className="border-b border-border/40">
                  <td className="py-2 pr-3 font-mono text-[12.5px]">perceptica.consent.v1</td>
                  <td className="py-2 pr-3">Stores your cookie consent choice (localStorage).</td>
                  <td className="py-2 pr-3">Strictly necessary</td>
                  <td className="py-2">Up to 12 months</td>
                </tr>
                <tr className="border-b border-border/40">
                  <td className="py-2 pr-3 font-mono text-[12.5px]">perceptica_locale</td>
                  <td className="py-2 pr-3">Remembers your selected language (EN / BG).</td>
                  <td className="py-2 pr-3">Strictly necessary</td>
                  <td className="py-2">12 months</td>
                </tr>
                <tr className="border-b border-border/40">
                  <td className="py-2 pr-3">Analytics cookies</td>
                  <td className="py-2 pr-3">Aggregated traffic and performance metrics.</td>
                  <td className="py-2 pr-3">Analytics</td>
                  <td className="py-2">Up to 13 months</td>
                </tr>
                <tr>
                  <td className="py-2 pr-3">Marketing cookies</td>
                  <td className="py-2 pr-3">Campaign measurement and attribution.</td>
                  <td className="py-2 pr-3">Marketing</td>
                  <td className="py-2">Up to 13 months</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h2 className="font-display mt-8 text-xl">5. Third parties and sub-processors</h2>
          <p>
            Analytics and marketing cookies may be set by trusted sub-processors (for example, hosting and
            analytics providers) bound by data processing agreements. We do not sell personal data.
          </p>

          <h2 className="font-display mt-8 text-xl">6. Your choices</h2>
          <p>
            When you first visit the site, a banner asks for your consent to non-essential cookies. You can
            change your choice at any time using the controls below, or by reopening the consent banner.
            You can also block or delete cookies through your browser settings, although doing so may
            affect how parts of the site work.
          </p>

          <h2 className="font-display mt-8 text-xl">7. Do Not Track and Global Privacy Control</h2>
          <p>
            We honour explicit privacy signals such as Global Privacy Control (GPC) where technically
            feasible, and treat them as a request to reject non-essential cookies.
          </p>

          <h2 className="font-display mt-8 text-xl">8. Retention</h2>
          <p>
            Your consent record is stored for up to 12 months, after which we ask again. Analytics and
            marketing data derived from cookies is retained for up to 13 months unless a shorter period
            applies.
          </p>

          <h2 className="font-display mt-8 text-xl">9. Changes to this policy</h2>
          <p>
            We may update this policy from time to time. Material changes will be reflected by a new
            "Last updated" date at the top of this page.
          </p>

          <h2 className="font-display mt-8 text-xl">10. Contact</h2>
          <p>
            For questions about cookies or your privacy choices, write to{" "}
            <a href="mailto:privacy@perceptica.com" className="underline underline-offset-2">privacy@perceptica.com</a>.
            See our <Link to="/privacy-policy" className="underline underline-offset-2">Privacy Policy</Link> for the
            full notice on how we process personal data.
          </p>

          <h2 className="font-display mt-10 text-xl">Manage your consent</h2>
          <p className="text-[13.5px] text-foreground/70">
            Current state: <strong>{consent ? `decided ${new Date(consent.decidedAt).toLocaleDateString()}` : "not set"}</strong>
          </p>
        </div>

        <div className="mt-6 flex flex-wrap gap-2">
          <Button onClick={acceptAll}>Accept all</Button>
          <Button variant="outline" onClick={rejectAll}>Reject non-essential</Button>
          <Button variant="ghost" onClick={reopen}>Reopen banner</Button>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
